import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Fade,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Popover,
} from '@mui/material';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { urlSafeString } from '@/utils/url';
import M24PageFinder from '@/components/Finder/M24PageFinder/M24PageFinder';
import { GenericMedia } from '@/declarations/GenericMedia';
import useConfirmDialog from '@/components/ConfirmDialogProvider';
import DateTimeInput from '@/components/forms/DateTimeInput';
import { deepCopy } from '@/utils/object';
import { Location } from '@/declarations/models/Location';
import SelectInput from '@/components/forms/SelectInput';
import { useStore } from '@/components/store/Store';
import { Page } from '@/declarations/models/Page';
import { Language } from '@/declarations/models/Language';
import { Api } from '@/services/Api';
import { formatAPITimestampLocal, isApiTimestampAtOrAfter } from '@/utils/dates';
import { PageRevision } from '@/declarations/models/PageRevision';
import { PageVersion } from '@/declarations/models/PageVersion';
import { Status } from '@/declarations/models/Status';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import { MainCategory } from '@/declarations/Category';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TextInput from '../../components/forms/TextInput';
import CheckboxInput from '../../components/forms/CheckboxInput';
import SectionsController from '../../components/Section/SectionsController';
import Section from '../../components/Section/Section';
import Container from '../../components/Container';
import { useEditorModel } from '../lib/components/EditorDataProvider';
import Styles from '../../assets/js/Styles';
import SwitchInput from '../../components/forms/SwitchInput';
import Loader from '../../components/Loader';
import PageEditorSaveButton from '../lib/components/PageEditorSaveButton';
import { useRestorePageContent } from './useRestorePageContent';
import CategoriesInfo from '../../components/CategoriesInfo';

export interface PageSettingsProps {
  open: boolean;
  onClose: () => void;
}

export const PageSettings: FC<PageSettingsProps & ChildrenProp> = ({ open, onClose }) => {
  const { t: tComp } = useTranslation('components');
  const { t: tCommon } = useTranslation('common');
  const { state } = useStore();
  const siteId = state.selectedSite?.id;
  const protectedStatus = useWatch<Page>({ name: `is_protected`, defaultValue: false });
  const lockedByParent = useWatch<Page, 'locked_by_parent'>({ name: 'locked_by_parent', defaultValue: false });
  const page = useEditorModel<Page>();
  const isTemplate = page.is_template;
  const [revisions, setRevisions] = useState<Array<PageRevision>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [editPathEnabled, setEditPathEnabled] = useState<boolean>(false);
  const originalPath = useRef(page?.path);
  const [templatePageFinderOpen, setTemplatePageFinderOpen] = useState(false);
  const [locations, setLocations] = useState<Array<Location>>();
  const { reset } = useFormContext<Page>();
  const confirm = useConfirmDialog();
  const selectedLocation = useWatch<Page, 'location'>({ name: 'location' });
  const [availableLanguages, setAvailableLanguages] = useState<Array<Language>>();
  const mainLanguageLocale = state.selectedSite?.default_language;
  const [languageLinksPageFinderOpen, setLanguageLinksPageFinderOpen] = useState(false);
  const [languageLinksFinderLanguage, setLanguageLinksFinderLanguage] = useState('');
  useEffect(() => {
    if (siteId) {
      const ctx = Api.getSiteLocations(siteId);
      ctx
        .fetchDirect(null)
        .then((l) => !!l && setLocations(l))
        .finally(ctx.abort);
    }
  }, [siteId]);

  useEffect(() => {
    if (state.selectedSite?.site_contents) {
      setAvailableLanguages(state.selectedSite.site_contents);
    } else {
      setAvailableLanguages([]);
    }
  }, [state.selectedSite?.site_contents]);

  const {
    field: { onChange: setPagePath },
  } = useController<Page, 'path'>({
    name: 'path',
  });

  const getVersionNumber = (version_id?: number) => {
    if (version_id) {
      const pageVersion = page.versions?.find((v) => v.id === version_id);
      return pageVersion?.version_number || '?';
    }
    return '?';
  };

  // A draft older than the published version can no longer be published, so it is shown as superseded
  const publishedVersionNumber = Math.max(
    ...(page.versions || []).filter((v) => v.status === Status.PUBLISHED).map((v) => v.version_number),
  );
  const isSuperseded = (version: PageVersion): boolean =>
    version.status === Status.DRAFT && version.version_number < publishedVersionNumber;
  // "Published" belongs to the page; the published version is the page's current version
  const getVersionStatusLabel = (version: PageVersion): string => {
    if (isSuperseded(version)) {
      return tComp('PageSettings.VersionsTable.Superseded');
    }
    if (version.status === Status.PUBLISHED) {
      return tComp('PageSettings.VersionsTable.Current');
    }
    return tCommon(`Status.${version.status}`);
  };

  // Languages this page is connected to, in the order the site lists its languages
  const connectedLanguageNames = (availableLanguages || [])
    .filter((language) => language.locale !== page.locale && page.language_links?.[language.locale]?.title)
    .map((language) => tCommon(`Languages.${language.locale}`));

  // The version open in the editor, which the save history is fetched for
  const editedVersion = page.versions?.find((v) => v.id === page.active_version_id);

  // Restoring is limited to superadmins for now. Templates and imported pages are saved through other flows.
  // With an open draft the button stays visible; useRestorePageContent tells the user to publish or delete it first.
  const canRestore = !!state.currentUser?.user?.is_super_admin && !isTemplate && page.status !== Status.IMPORTED;
  const restorePageContent = useRestorePageContent(onClose);

  // The preview button last used stays highlighted, so the right revision or version is restored afterwards
  const [previewedRow, setPreviewedRow] = useState<string | null>(null);

  // The version visitors see: the page is published (not planned for later) and this is its published version
  const isPlannedPublish = !!page.published_at && isApiTimestampAtOrAfter(page.published_at);
  const isLiveVersion = (versionId?: number): boolean =>
    page.status === Status.PUBLISHED &&
    !isPlannedPublish &&
    page.versions?.find((v) => v.id === versionId)?.status === Status.PUBLISHED;

  // What visitors see opens at the page's normal URL, anything else in the portal's preview.
  // The portal checks created_at against the revision's own created_at when revision_id is given.
  const openPreview = (
    rowKey: string,
    isLive: boolean,
    { versionId, revisionId, createdAt }: { versionId?: number; revisionId?: number; createdAt?: string },
  ) => {
    const baseUrl = (page?.base_url || '').replace(/^https?:\/\//, '');
    let url = `//${page.url || `${baseUrl}/${page?.path || ''}`}`;
    if (!isLive) {
      const urlParts = baseUrl.split('/');
      const siteDomain = urlParts[0] || '';
      const sitePath = urlParts.slice(1).join('/') || '';
      url = `//${siteDomain}/_m24_/preview?path=${sitePath}/${page?.path}&page_id=${page.id}&version_id=${versionId}`;
      if (revisionId) url += `&revision_id=${revisionId}`;
      url += `&created_at=${createdAt}`;
    }
    setPreviewedRow(rowKey);
    window.open(url, '_blank');
  };
  // type versionPath = `version[0}].${string}`;
  /*
  const getVersionIndex = (version: PageVersion): number => {
    return page.versions?.findIndex((v) => v.id === version.id) || 0;
  };
  */

  useEffect(() => {
    if (!open) {
      return;
    }
    let unmounted = false;
    setIsLoading(true);

    const ctx = Api.getPageRevisions(page?.id || 0, 0, 10, page?.active_version_id || 0);
    ctx
      .fetchDirect(null)
      .then((fetchedRevisions) => {
        if (!unmounted && fetchedRevisions) {
          setRevisions(fetchedRevisions);
        }
      })
      .finally(() => {
        if (!unmounted) {
          setIsLoading(false);
        }
      });
    return () => {
      unmounted = true;
      ctx.abort();
    };
  }, [open, page?.active_version_id, page?.id, page?.updated_at]);

  const onTemplateSelected = async (selected: Array<GenericMedia<Page>>) => {
    const [selectedTemplate] = selected;
    if (!selectedTemplate.source?.id) {
      // console.warn('no template selected');
      return;
    }
    const template = await Api.getPageTemplate(selectedTemplate.source.id!).fetchDirect(null);
    if (!template?.content) {
      // console.warn('template missing content');
      return;
    }

    if (!(await confirm(tComp('PageSettings.ConfirmApplyTemplate')))) {
      return;
    }

    if (template.insert_title) {
      page.insert_title = template.insert_title;
    }

    const content = deepCopy(template.content);
    const p = {
      ...page,
      content,
    };
    reset(p);
    // setInitialFormData(page);
    // eslint-disable-next-line no-restricted-syntax
    // for (const [name, value] of Object.entries(p || {})) {
    // setValue(name, value); // Don't use reset(), it causes input fields getting incorrect data from the form's defaultValues after reordering
    // }

    onClose();
  };

  const {
    field: { value: languageLinks, onChange: setLanguageLinks },
  } = useController<Page, 'language_links'>({ name: 'language_links' });

  const onLanguageLinksSelected = async (selected: Array<GenericMedia<Page>>) => {
    const [selectedPage] = selected;
    const newLanguageLinks = {
      ...languageLinks,
      [selectedPage.source?.locale]: selectedPage.source,
    };
    setLanguageLinks(newLanguageLinks);
  };

  const onDeleteLanugageLink = (locale: string) => {
    const newLanguageLinks = {
      ...languageLinks,
      [locale]: {},
    };
    setLanguageLinks(newLanguageLinks);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpenPublishingInfo = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePublishingInfo = () => {
    setAnchorEl(null);
  };

  const publishingInfoOpen = Boolean(anchorEl);

  return (
    <Modal open={open} onClose={onClose}>
      <Fade in={open}>
        <Container p={4} fullWidth fullHeight top>
          <Paper
            sx={{
              backgroundColor: Styles.Colors.THEME_BG_COLOR,
              width: '100%',
              position: 'relative',
              overflow: 'auto',
              maxHeight: '94vh',
            }}>
            <Container fullWidth right px={2} py={2}>
              <PageEditorSaveButton options={[]} />
              <Button
                variant='contained'
                color='secondary'
                startIcon={<MaterialSymbol name='close' />}
                onClick={onClose}>
                {tComp('PageSettings.Close')}
              </Button>
            </Container>
            <SectionsController px={2} defaultExpanded={['general']}>
              {/* TODO: Set season */}
              {/* TODO: Set DiMu ID */}
              {/* TODO: Toggle is-search-result-page */}
              <Section sectionId='general' title={tComp('PageSettings.Sections.general')}>
                <Container gap={2} column top left fullWidth>
                  <TextInput<Page> path='title' label={tComp('PageSettings.FormLabels.Title')} required />
                  <TextInput<Page>
                    key={`path-${page?.id}-${editPathEnabled ? 'enabled' : 'disabled'}`}
                    path='path'
                    label={tComp('PageSettings.FormLabels.Slug')}
                    required
                    disabled={!editPathEnabled}
                    inputFormatter={(val) => urlSafeString(val, '/')}
                  />
                  {!editPathEnabled && (
                    <Button onClick={() => setEditPathEnabled(true)}>{tComp('PageSettings.EnableEditPath')}</Button>
                  )}
                  {editPathEnabled && (
                    <Button
                      onClick={() => {
                        setPagePath(originalPath.current);
                        setEditPathEnabled(false);
                      }}>
                      {tComp('PageSettings.CancelEditPath', { old: originalPath.current })}
                    </Button>
                  )}
                  <Typography>
                    <b>{tComp('PageSettings.Category')}: </b>
                    {tCommon(`MainCategory.${page.main_category ?? ''}`)}
                  </Typography>
                  {page.category?.tag && (
                    <>
                      <Typography>
                        <b>{tComp('PageSettings.CategoryOld')}: </b>
                        {page.category?.tag ?? ''}
                      </Typography>
                    </>
                  )}
                  <CategoriesInfo />

                  <CheckboxInput<Page> path='content.isLongread' label={tComp('PageSettings.FormLabels.IsLongread')} />
                  {page.content?.isLongread && (
                    <>
                      <TextInput<Page>
                        path='content.longreadsettings.logotext'
                        label={tComp('PageSettings.LongreadLogoText')}
                      />
                      <TextInput<Page>
                        path='content.longreadsettings.logolink'
                        label={tComp('PageSettings.LongreadLogoLink')}
                      />
                    </>
                  )}

                  {(() => {
                    const activeVersion = page.versions?.find((v) => v.id === page.active_version_id);
                    return (
                      <>
                        <Typography>
                          <b>
                            {tComp('PageSettings.SelectTemplateLabel')}
                            {activeVersion?.status !== 'draft' &&
                              page.status !== 'draft' &&
                              ` (${tComp('PageSettings.SelectTemplateDisabled')})`}
                          </b>
                        </Typography>
                        <Button
                          disabled={activeVersion?.status !== 'draft' && page.status !== 'draft'}
                          variant='contained'
                          color='secondary'
                          sx={{ flex: '1 0 auto', mt: 1 }}
                          onClick={() => setTemplatePageFinderOpen(true)}>
                          {tComp('PageSettings.SelectTemplateButton')}
                        </Button>
                      </>
                    );
                  })()}
                  <M24PageFinder
                    open={templatePageFinderOpen}
                    onClose={() => setTemplatePageFinderOpen(false)}
                    onSelectionConfirmed={onTemplateSelected}
                    type='template'
                    multiSelect={false}
                  />

                  <Alert severity='info' sx={{ width: '100%' }}>
                    {tComp('PageSettings.SelectTemplateDescription')}
                  </Alert>

                  <Box
                    sx={{
                      mt: 2,
                      minWidth: '265px',
                    }}>
                    {/* TODO: Choose location */}
                    <Container fullWidth column gap={2}>
                      {locations && (
                        <SelectInput
                          // hack(MV-737): 'location' is (only sometimes...) reset to the original when it gets removed
                          //  solution: key the component with the selected location.
                          //  unfortunately this breaks focus when selection changes, so keyboard users will get mad
                          //  todo: find a proper solution
                          key={selectedLocation?.id ?? 0}
                          options={locations}
                          getOptionKey={(l) => (l ? `${l.id},${l.id}` : '')}
                          getOptionLabel={(l) => String(l?.title)}
                          path='location'
                          label={tComp('PageSettings.Locations')}
                          disabled={!locations.length}
                          defaultValue=''
                          size='small'
                        />
                      )}
                    </Container>
                  </Box>
                  <Container
                    fullWidth
                    column
                    gap={2}
                    sx={{
                      mt: 2,
                      alignItems: 'flex-start',
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mt: 2,
                        width: '100%',
                      }}>
                      <Box>
                        <DateTimeInput path='published_at' label='Publiseringsdato' inputType='date' />
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          bgcolor: 'grey.100',
                          minHeight: 56,
                        }}>
                        <InfoOutlinedIcon fontSize='small' />

                        <Typography variant='body2'>
                          {tComp('PageSettings.PublishDateTip')}{' '}
                          <Button
                            type='button'
                            onClick={handleOpenPublishingInfo}
                            sx={{
                              p: 0,
                              border: 0,
                              background: 'none',
                              font: 'inherit',
                              cursor: 'pointer',
                              verticalAlign: 'baseline',
                              textDecoration: 'underline',
                              minWidth: 'unset',
                              display: 'inline',
                              lineHeight: 'inherit',
                            }}>
                            {tComp('PageSettings.PublishDateTipLearnMore')}
                          </Button>
                        </Typography>
                      </Box>

                      <Popover
                        open={publishingInfoOpen}
                        anchorEl={anchorEl}
                        onClose={handleClosePublishingInfo}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}>
                        <Box sx={{ p: 2, maxWidth: 420 }}>
                          <Typography variant='body2'>{tComp('PageSettings.PublishDateTipDescription')}</Typography>
                        </Box>
                      </Popover>
                    </Box>
                  </Container>
                </Container>
              </Section>
              <Section
                sectionId='languageConnections'
                title={tComp('PageSettings.Sections.languageConnections')}
                subTitleMaxWidth={400}
                subTitle={
                  connectedLanguageNames.length > 0 &&
                  tComp('PageSettings.LanguageConnections.ConnectedTo', {
                    languages: connectedLanguageNames.join(', '),
                  })
                }>
                {page.locale === mainLanguageLocale && (
                  <Typography sx={{ maxWidth: 1000 }}>{tComp('PageSettings.LanguageConnections.Intro')}</Typography>
                )}
                {page.locale !== mainLanguageLocale && (
                  <Typography sx={{ maxWidth: 1000 }}>
                    {tComp('PageSettings.LanguageConnections.IntroNotMainLanguage')}
                  </Typography>
                )}

                <TableContainer
                  component={Paper}
                  style={{
                    border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
                    borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
                    marginTop: '20px',
                  }}>
                  <Table size='medium'>
                    <TableHead>
                      <TableRow>
                        <TableCell colSpan={2}>{tComp('PageSettings.LanguageConnections.OtherLanguages')}</TableCell>
                        <TableCell colSpan={2}> {tComp('PageSettings.LanguageConnections.PageConnections')}</TableCell>
                      </TableRow>
                    </TableHead>
                    {availableLanguages
                      ?.filter((language) => language.status !== 'draft' && language.locale !== page.locale)
                      .map((language) => (
                        <TableRow
                          key={language.locale}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}>
                          <TableCell width={250} sx={{ fontWeight: 500 }}>
                            {tCommon(`Languages.${language.locale}`)}
                            {language.locale === mainLanguageLocale && (
                              <strong> ({tComp('PageSettings.LanguageConnections.MainLanguage')})</strong>
                            )}
                          </TableCell>
                          <TableCell width={200}>
                            {page.locale !== language.locale && page.locale === mainLanguageLocale && (
                              <Button
                                variant='contained'
                                color='secondary'
                                disabled={language.locale === page.locale || page.locale !== mainLanguageLocale}
                                onClick={() => {
                                  setLanguageLinksFinderLanguage(language.locale);
                                  setLanguageLinksPageFinderOpen(true);
                                }}>
                                {tComp('PageSettings.LanguageConnections.ConnectPage')}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell width={300}>
                            {(page?.language_links &&
                              page.language_links[language.locale] &&
                              page.language_links[language.locale].title) ||
                              tComp('PageSettings.LanguageConnections.NoConnection')}
                          </TableCell>
                          <TableCell>
                            {page.locale === mainLanguageLocale &&
                              page?.language_links &&
                              page.language_links[language.locale] &&
                              page.language_links[language.locale].title && (
                                <Button
                                  variant='contained'
                                  color='secondary'
                                  disabled={page.locale !== mainLanguageLocale}
                                  onClick={() => {
                                    onDeleteLanugageLink(language.locale);
                                  }}>
                                  {tComp('PageSettings.LanguageConnections.RemoveConnection')}
                                </Button>
                              )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </Table>
                </TableContainer>

                <M24PageFinder
                  open={languageLinksPageFinderOpen}
                  onClose={() => setLanguageLinksPageFinderOpen(false)}
                  onSelectionConfirmed={onLanguageLinksSelected}
                  type='page'
                  isEvent={page.main_category === MainCategory.EVENT}
                  multiSelect={false}
                  lockedLanguage={languageLinksFinderLanguage}
                />
              </Section>
              <Section
                sectionId='revisions'
                title={tComp('PageSettings.Sections.revisions')}
                subTitleMaxWidth={400}
                subTitle={
                  editedVersion &&
                  tComp('PageSettings.RevisionsTable.EditedVersion', {
                    number: editedVersion.version_number,
                    title: editedVersion.title,
                    status: getVersionStatusLabel(editedVersion),
                  })
                }>
                {isLoading ? (
                  <Loader size={25} />
                ) : (
                  <>
                    <Typography sx={{ maxWidth: 1000 }}>
                      {/* A backend without include_active support leaves out the current revision */}
                      {revisions.some((r) => r.is_active)
                        ? tComp('PageSettings.RevisionsTable.InfoTextWithCurrent')
                        : tComp('PageSettings.RevisionsTable.InfoText')}
                    </Typography>
                    <TableContainer
                      component={Paper}
                      style={{
                        border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
                        borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
                        marginTop: '20px',
                      }}>
                      <Table sx={{ minWidth: 650 }} size='medium'>
                        <TableHead sx={{ backgroundColor: Styles.Colors.THEME_BG_COLOR_SECONDARY }}>
                          <TableRow>
                            <TableCell>{tComp('PageSettings.RevisionsTable.SavedTimestamp')}</TableCell>
                            <TableCell>{tComp('PageSettings.RevisionsTable.CreatedBy')}</TableCell>
                            <TableCell>{tComp('PageSettings.RevisionsTable.RevisionID')}</TableCell>
                            <TableCell>{tComp('PageSettings.RevisionsTable.Version')}</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {revisions?.map((revision) => (
                            <TableRow
                              key={revision.id}
                              sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                ...(revision.is_active && { backgroundColor: Styles.Colors.MEDIUM_GREEN_TRANSPARENT }),
                              }}>
                              <TableCell component='th' scope='row'>
                                {formatAPITimestampLocal(revision.created_at, 'datetime')}
                                {revision.is_active && ` (${tComp('PageSettings.RevisionsTable.Current')})`}
                              </TableCell>
                              <TableCell>
                                {revision.created_by_name} ({revision.created_by_id})
                              </TableCell>
                              <TableCell>{revision.id}</TableCell>
                              <TableCell>{getVersionNumber(revision?.page_version_id)}</TableCell>
                              <TableCell>
                                <Button
                                  variant={previewedRow === `revision-${revision.id}` ? 'contained' : 'text'}
                                  onClick={() =>
                                    openPreview(
                                      `revision-${revision.id}`,
                                      !!revision.is_active && isLiveVersion(revision.page_version_id),
                                      {
                                        versionId: revision.page_version_id,
                                        revisionId: revision.id,
                                        createdAt: revision.created_at,
                                      },
                                    )
                                  }>
                                  <MaterialSymbol name='preview' sx={{ fontSize: 30 }} />
                                </Button>
                                {canRestore && !revision.is_active && (
                                  <Button
                                    sx={{ backgroundColor: '#fbe6ff', '&:hover': { backgroundColor: '#f3ccff' } }}
                                    title={tComp('PageSettings.Restore.Button')}
                                    aria-label={tComp('PageSettings.Restore.Button')}
                                    onClick={() =>
                                      restorePageContent({
                                        label: tComp('PageSettings.Restore.RevisionLabel', {
                                          id: revision.id,
                                          time: formatAPITimestampLocal(revision.created_at, 'datetime'),
                                        }),
                                        loadContent: async () => revision.content,
                                      })
                                    }>
                                    <MaterialSymbol name='restore' sx={{ fontSize: 30 }} />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Section>
              <Section
                sectionId='versions'
                title={tComp('PageSettings.Sections.versions')}
                subTitle={
                  !!page.versions?.length && tComp('PageSettings.VersionsTable.Count', { count: page.versions.length })
                }>
                <Typography sx={{ maxWidth: 1000 }}>{tComp('PageSettings.VersionsTable.InfoText')}</Typography>
                <TableContainer
                  component={Paper}
                  style={{
                    border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
                    borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
                    marginTop: '20px',
                  }}>
                  <Table sx={{ minWidth: 650 }} size='medium'>
                    <TableHead sx={{ backgroundColor: Styles.Colors.THEME_BG_COLOR_SECONDARY }}>
                      <TableRow>
                        <TableCell>{tComp('PageSettings.VersionsTable.VersionID')}</TableCell>
                        <TableCell>{tComp('PageSettings.VersionsTable.OptVersionTitle')}</TableCell>
                        <TableCell>{tComp('PageSettings.VersionsTable.Choosen')}</TableCell>
                        <TableCell>{tComp('PageSettings.VersionsTable.VersionStatus')}</TableCell>
                        <TableCell>{tComp('PageSettings.VersionsTable.Created')}</TableCell>
                        <TableCell>{tComp('PageSettings.VersionsTable.CreatedBy')}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...(page.versions || [])]
                        .sort((a, b) => b.version_number - a.version_number)
                        .map((version) => (
                          <TableRow
                            key={version.id}
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                              ...(version.status === Status.PUBLISHED && {
                                backgroundColor: Styles.Colors.MEDIUM_GREEN_TRANSPARENT,
                              }),
                            }}>
                            <TableCell component='th' scope='row'>
                              {version.version_number} ({version.id})
                            </TableCell>
                            <TableCell>{version.title}</TableCell>
                            <TableCell>
                              {version.id === page.active_version_id
                                ? tComp('PageSettings.VersionsTable.Yes')
                                : tComp('PageSettings.VersionsTable.No')}
                            </TableCell>
                            <TableCell>{getVersionStatusLabel(version)}</TableCell>
                            <TableCell>{formatAPITimestampLocal(version.created_at, 'datetime')}</TableCell>
                            <TableCell>
                              {version.created_by_name
                                ? `${version.created_by_name} (${version.created_by_id})`
                                : version.created_by_id}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant={previewedRow === `version-${version.id}` ? 'contained' : 'text'}
                                onClick={() =>
                                  openPreview(`version-${version.id}`, isLiveVersion(version.id), {
                                    versionId: version.id,
                                    createdAt: version.created_at,
                                  })
                                }>
                                <MaterialSymbol name='preview' sx={{ fontSize: 30 }} />
                              </Button>
                              {canRestore && version.id !== page.active_version_id && isSuperseded(version) && (
                                <Button
                                  sx={{ backgroundColor: '#fbe6ff', '&:hover': { backgroundColor: '#f3ccff' } }}
                                  title={tComp('PageSettings.Restore.Button')}
                                  aria-label={tComp('PageSettings.Restore.Button')}
                                  onClick={() =>
                                    restorePageContent({
                                      label: tComp('PageSettings.Restore.VersionLabel', {
                                        number: version.version_number,
                                      }),
                                      loadContent: async () =>
                                        (await Api.getPage(page.id!, { version_id: version.id }).fetchDirect(null))
                                          ?.content,
                                    })
                                  }>
                                  <MaterialSymbol name='restore' sx={{ fontSize: 30 }} />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Section>
              <Section
                sectionId='security'
                title={tComp('PageSettings.Sections.security')}
                subTitle={
                  (lockedByParent && tComp('PageSettings.ProtectedByParent')) ||
                  (protectedStatus && tComp('PageSettings.Protected'))
                }>
                <Container gap={2} column top left fullWidth>
                  {lockedByParent ? (
                    <>
                      <Alert severity='info' sx={{ width: '100%' }}>
                        {tComp('PageSettings.LockedByParentInfo')}
                      </Alert>
                      <TextInput<Page>
                        path='access_code'
                        label={tComp('PageSettings.FormLabels.accessCode')}
                        defaultValue=''
                      />
                    </>
                  ) : (
                    <>
                      <SwitchInput<Page> path='is_protected' label={tComp('PageSettings.FormLabels.isProtected')} />
                      <TextInput<Page>
                        path='access_code'
                        label={tComp(
                          protectedStatus
                            ? 'PageSettings.FormLabels.pageAlreadyHasAccessCode'
                            : 'PageSettings.FormLabels.accessCode',
                        )}
                        disabled={!protectedStatus}
                        defaultValue=''
                      />
                      <TextInput<Page>
                        path='content.protectedTitle'
                        label={tComp('PageSettings.FormLabels.protectedTitle')}
                        disabled={!protectedStatus}
                        defaultValue=''
                      />
                      <TextInput<Page>
                        path='content.protectedIntro'
                        label={tComp('PageSettings.FormLabels.protectedIntro')}
                        disabled={!protectedStatus}
                        defaultValue=''
                      />
                      <TextInput<Page>
                        path='content.protectedError'
                        label={tComp('PageSettings.FormLabels.protectedError')}
                        disabled={!protectedStatus}
                        defaultValue=''
                      />
                      <TextInput<Page>
                        path='content.protectedLabel'
                        label={tComp('PageSettings.FormLabels.protectedLabel')}
                        disabled={!protectedStatus}
                        defaultValue=''
                      />
                      <TextInput<Page>
                        path='content.protectedButton'
                        label={tComp('PageSettings.FormLabels.protectedButton')}
                        disabled={!protectedStatus}
                        defaultValue=''
                      />
                    </>
                  )}
                </Container>
              </Section>
            </SectionsController>
            <Container fullWidth right px={2} py={2}>
              <PageEditorSaveButton options={[]} />
              <Button
                variant='contained'
                color='secondary'
                startIcon={<MaterialSymbol name='close' />}
                onClick={onClose}>
                {tComp(`PageSettings.${isTemplate ? 'CloseTemplateSettings' : 'Close'}`)}
              </Button>
            </Container>
          </Paper>
        </Container>
      </Fade>
    </Modal>
  );
};

export default PageSettings;
